import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  useProjectCreateCadLayerMutation,
  useProjectDeleteCadLayerMutation,
  useProjectDeleteCadObjectMutation,
  useProjectDeleteCadObjectGroupMutation,
  useProjectHideCadObjectMutation,
  useProjectConvertCadObjectMutation,
  useProjectCreateWmsLayerMutation,
  useProjectDeleteWmsLayerMutation,
  useProjectUpdateWmsLayerMutation,
  useProjectUpdateWmsLayerOrderingMutation,
  useProjectCreateOrthophotoLayerMutation,
  useProjectDeleteOrthophotoLayerMutation,
  useProjectUpdateOrthophotoLayerOrderingMutation,
  WmsType,
  InputCadObject,
} from '../../types/graphqlTypes';

export function useLayerOperations() {
  const { projectId } = useParams<{ projectId: string }>();
  const [createCadLayerMut] = useProjectCreateCadLayerMutation();
  const [deleteCadLayerMut] = useProjectDeleteCadLayerMutation();
  const [deleteCadObjectMut] = useProjectDeleteCadObjectMutation();
  const [deleteCadObjectGroupMut] = useProjectDeleteCadObjectGroupMutation();
  const [hideCadObjectMut] = useProjectHideCadObjectMutation();
  const [convertCadObjectMut] = useProjectConvertCadObjectMutation();
  const [createWmsLayerMut] = useProjectCreateWmsLayerMutation();
  const [deleteWmsLayerMut] = useProjectDeleteWmsLayerMutation();
  const [updateWmsLayerMut] = useProjectUpdateWmsLayerMutation();
  const [updateWmsLayerOrderingMut] = useProjectUpdateWmsLayerOrderingMutation();
  const [createOrthophotoLayerMut] = useProjectCreateOrthophotoLayerMutation();
  const [deleteOrthophotoLayerMut] = useProjectDeleteOrthophotoLayerMutation();
  const [updateOrthophotoLayerOrderingMut] = useProjectUpdateOrthophotoLayerOrderingMutation();

  // CAD
  const createCadLayer = useCallback(
    async (args: { cadLayerId: string; name: string; fileName: string }) => {
      if (!projectId) return;
      await createCadLayerMut({ variables: { projectId, ...args } });
    },
    [projectId, createCadLayerMut],
  );

  const deleteCadLayer = useCallback(
    async (cadLayerIdentifier: string) => {
      if (!projectId) return;
      await deleteCadLayerMut({ variables: { projectId, cadLayerIdentifier } });
    },
    [projectId, deleteCadLayerMut],
  );

  const deleteCadObject = useCallback(
    async (cadLayerIdentifier: string, index: number, subIndex?: number | null) => {
      if (!projectId) return;
      await deleteCadObjectMut({ variables: { projectId, cadLayerIdentifier, index, subIndex } });
    },
    [projectId, deleteCadObjectMut],
  );

  const deleteCadObjectGroup = useCallback(
    async (cadLayerIdentifier: string, index: number) => {
      if (!projectId) return;
      await deleteCadObjectGroupMut({ variables: { projectId, cadLayerIdentifier, index } });
    },
    [projectId, deleteCadObjectGroupMut],
  );

  const hideCadObject = useCallback(
    async (args: {
      cadLayerIdentifier: string;
      index: number;
      subIndex?: number | null;
      isGroup?: boolean | null;
      isLayer?: boolean | null;
      visible: boolean;
    }) => {
      if (!projectId) return;
      await hideCadObjectMut({ variables: { projectId, ...args } });
    },
    [projectId, hideCadObjectMut],
  );

  const convertCadObject = useCallback(
    async (cadObjects: InputCadObject[]) => {
      if (!projectId) return;
      await convertCadObjectMut({ variables: { projectId, cadObjects } });
    },
    [projectId, convertCadObjectMut],
  );

  // WMS
  const createWmsLayer = useCallback(
    async (args: { layerId: string; name: string; url: string; username?: string; password?: string; layerName: string; type: WmsType }) => {
      if (!projectId) return;
      await createWmsLayerMut({ variables: { projectId, ...args } });
    },
    [projectId, createWmsLayerMut],
  );

  const deleteWmsLayer = useCallback(
    async (wmsLayerIdentifier: string) => {
      if (!projectId) return;
      await deleteWmsLayerMut({ variables: { projectId, wmsLayerIdentifier } });
    },
    [projectId, deleteWmsLayerMut],
  );

  const updateWmsLayer = useCallback(
    async (args: { layerId: string; name: string; url: string; username?: string; password?: string; layerName: string; type: WmsType }) => {
      if (!projectId) return;
      await updateWmsLayerMut({ variables: { projectId, ...args } });
    },
    [projectId, updateWmsLayerMut],
  );

  const updateWmsLayerOrdering = useCallback(
    async (identifiers: string[], toIndex: number) => {
      if (!projectId) return;
      await updateWmsLayerOrderingMut({ variables: { projectId, identifiers, toIndex } });
    },
    [projectId, updateWmsLayerOrderingMut],
  );

  // Orthophoto
  const createOrthophotoLayer = useCallback(
    async (args: { layerId: string; name: string }) => {
      if (!projectId) return;
      await createOrthophotoLayerMut({ variables: { projectId, ...args } });
    },
    [projectId, createOrthophotoLayerMut],
  );

  const deleteOrthophotoLayer = useCallback(
    async (orthophotoLayerIdentifier: string) => {
      if (!projectId) return;
      await deleteOrthophotoLayerMut({ variables: { projectId, orthophotoLayerIdentifier } });
    },
    [projectId, deleteOrthophotoLayerMut],
  );

  const updateOrthophotoLayerOrdering = useCallback(
    async (identifiers: string[], toIndex: number) => {
      if (!projectId) return;
      await updateOrthophotoLayerOrderingMut({ variables: { projectId, identifiers, toIndex } });
    },
    [projectId, updateOrthophotoLayerOrderingMut],
  );

  return {
    createCadLayer, deleteCadLayer, deleteCadObject, deleteCadObjectGroup, hideCadObject, convertCadObject,
    createWmsLayer, deleteWmsLayer, updateWmsLayer, updateWmsLayerOrdering,
    createOrthophotoLayer, deleteOrthophotoLayer, updateOrthophotoLayerOrdering,
  };
}
